import { AiFieldMetrics, AiFieldMetricState } from '../../types/template-types';
import { TemplateState } from '../templateeditor-types';
import { getField } from '../selectors';
import { AiTemplateBuilderPayload } from './templateeditor-ai-types';

/**
 * Sets up the AI metrics for the template state if they are not already set.
 * Increments the analyze count each time this function is called.
 *
 * @param state The current template state.
 */
export function trackAnalysis(state: TemplateState): TemplateState {
  // check to see if the ai metrics are already set up, if not, set them up
  if (!state.template.aiMetrics) {
    state.template.aiMetrics = {
      analyzeCount: 0,
      saveCount: 0,
      fieldMetrics: {}
    };
  }

  state.template.aiMetrics.analyzeCount += 1;

  return state;
}

export function incrememtSaveCount(state: TemplateState): TemplateState {
  if (!state.template.aiMetrics) {
    return state;
  }

  state.template.aiMetrics.saveCount += 1;

  return state;
}

/**
 * Updates the AI field metrics for a specific field in the template.
 */
export function updateAiFieldMetrics(
  state: TemplateState,
  fieldId: string,
  metricState: AiFieldMetricState,
  /** The property to update. */
  prop: keyof AiFieldMetrics,
  /**
   * True, to set the `changedTo` value even if the field is already tracked, false otherwise.
   * Default: `false`
   */
  overrideTracked = false
): TemplateState {
  const field = getField(state, fieldId);

  if (!field?.isAiPlaced) {
    return state;
  }

  const fieldMetrics: AiFieldMetrics = state.template.aiMetrics?.fieldMetrics[fieldId] ?? {
    [prop]: AiFieldMetricState.NoChange
  };

  if (!overrideTracked && fieldMetrics[prop] === AiFieldMetricState.Tracked) {
    return state;
  }

  const updatedFieldMetrics: AiFieldMetrics = {
    ...fieldMetrics,
    [prop]: metricState
  };

  state.template.aiMetrics.fieldMetrics[fieldId] = updatedFieldMetrics;

  return state;
}

export function markAsTracked(state: TemplateState): TemplateState {
  if (!state.template.aiMetrics) {
    return state;
  }

  // mark all field metrics that are changed as tracked
  Object.entries(state.template.aiMetrics.fieldMetrics).forEach(([_, metrics]) => {
    Object.keys(metrics).forEach((prop: keyof AiFieldMetrics) => {
      // if the metric is changed, mark it as tracked
      if (metrics[prop] === AiFieldMetricState.Changed) {
        metrics[prop] = AiFieldMetricState.Tracked;
      }
    });
  });

  return state;
}

export function addExistingMetrics(state: TemplateState, payload: Pick<AiTemplateBuilderPayload, 'aiMetrics' | 'transientAiFields'>): TemplateState {
  if (!state.template) {
    return state;
  }

  const { aiMetrics, transientAiFields = [] } = payload;
  const transientFieldIds = transientAiFields.map((field) => field.id);

  state.template.aiMetrics = aiMetrics;
  // at this point we need to look for fields that are tracked in the metrics and mark them as
  state.template.fields
    ?.filter((field) => transientFieldIds.includes(field.id))
    .forEach((field) => {
      const transientField = transientAiFields.find((f) => f.id === field.id);

      field.isAiPlaced = transientField?.isAiPlaced ?? false;
      field.aiCheckboxGroupProperties = transientField?.aiCheckboxGroupProperties;
    });

  return state;
}
